// Section quản trị ĐỊA BÀN (SCR-050 tab "Địa bàn"): đối xứng OrganizationAdminSection,
// chỉ khác adapter DTO (`level` thay `type`) và nhãn nghiệp vụ.

import { App as AntdApp } from "antd";
import {
  closeAdministrativeArea,
  createAdministrativeArea,
  getAdministrativeAreaDetail,
  getAdministrativeAreaHistory,
  getAdministrativeAreaTree,
  mergeAdministrativeArea,
  openAdministrativeArea,
  renameAdministrativeArea,
  transferAdministrativeArea,
  type AdministrativeAreaDetail,
  type AdministrativeAreaTreeNode,
  type AdministrativeAreaVersionView,
} from "./administrativeAreaAdminApi";
import type { AdminCommandOutcome, AdminDetail, AdminNode, AdminVersion, AdminWorkspaceApi } from "./adminTypes";
import { AdminWorkspace } from "./components/AdminWorkspace";

function toNode(node: AdministrativeAreaTreeNode): AdminNode {
  return {
    id: node.id,
    code: node.code,
    name: node.name,
    classification: node.level,
    parentId: node.parentId ?? null,
    status: node.status,
  };
}

function toDetail(detail: AdministrativeAreaDetail): AdminDetail {
  return { ...toNode(detail), validFrom: detail.validFrom, validTo: detail.validTo ?? null, version: detail.version };
}

function toVersion(version: AdministrativeAreaVersionView): AdminVersion {
  return {
    version: version.version,
    name: version.name,
    classification: version.level,
    parentId: version.parentId ?? null,
    status: version.status,
    validFrom: version.validFrom,
    validTo: version.validTo ?? null,
  };
}

function toOutcome(outcome: { id: string; version: number; rehomedChildIds?: string[] | undefined }): AdminCommandOutcome {
  return { id: outcome.id, version: outcome.version, rehomedChildIds: outcome.rehomedChildIds };
}

/** exactOptionalPropertyTypes: chỉ gửi `reason` khi có giá trị (request DTO khai báo `reason?: string`). */
function reasonField(reason: string | undefined): { reason?: string } {
  return reason != null ? { reason } : {};
}

const administrativeAreaAdminApi: AdminWorkspaceApi = {
  getTree: (signal) => getAdministrativeAreaTree(undefined, signal).then((nodes) => nodes.map(toNode)),
  getDetail: (id, signal) => getAdministrativeAreaDetail(id, undefined, signal).then(toDetail),
  getHistory: (id, signal) => getAdministrativeAreaHistory(id, signal).then((versions) => versions.map(toVersion)),
  create: (input, key) =>
    createAdministrativeArea(
      {
        code: input.code,
        name: input.name,
        level: input.classification,
        parentId: input.parentId ?? null,
        validFrom: input.validFrom,
        reason: input.reason,
      },
      key,
    ).then(toOutcome),
  rename: (input, key) =>
    renameAdministrativeArea(
      input.id,
      {
        expectedVersion: input.expectedVersion,
        newName: input.newName,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  transfer: (input, key) =>
    transferAdministrativeArea(
      input.id,
      {
        expectedVersion: input.expectedVersion,
        newParentId: input.newParentId,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  merge: (input, key) =>
    mergeAdministrativeArea(
      input.survivorId,
      {
        expectedVersion: input.survivorExpectedVersion,
        absorbedId: input.absorbedId,
        absorbedExpectedVersion: input.absorbedExpectedVersion,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  close: (input, key) =>
    closeAdministrativeArea(
      input.id,
      {
        expectedVersion: input.expectedVersion,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  open: (input, key) =>
    openAdministrativeArea(
      input.id,
      { expectedVersion: input.expectedVersion, effectiveFrom: input.effectiveFrom, reason: input.reason },
      key,
    ).then(toOutcome),
};

export function AdministrativeAreaAdminSection() {
  return (
    <AntdApp>
      <AdminWorkspace entityNoun="địa bàn" classificationLabel="Cấp hành chính" api={administrativeAreaAdminApi} />
    </AntdApp>
  );
}
